/* eslint-disable @typescript-eslint/no-magic-numbers */
import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  EMPTY,
  filter,
  from,
  iif,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DeepgramFeature, EnhancePatientNotePromptType, PatientNotesDataService } from '@ffd/api-client';
import { ConfigService, HistoryStackService, HistoryElementWithCursor, ToastService, TokenService } from '@libs/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { DictationSink, VoiceInputType, createVoiceChannel } from '@libs/voice-input';
import { Dialog } from '@angular/cdk/dialog';
import { fromReadableStream } from 'libs/notes/src/lib/components/notes-screen/notes-utils';
import { DataState, routes } from '@libs/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { HealthHistoryDialogService } from '@libs/home';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';

const MODIFY_NOTE_DEBOUNCE_TIME_MS = 1000;

export interface PatientNotesHistoryElement extends HistoryElementWithCursor {
  patientNotes: string;
}

interface HealthHistoryNotesState {
  siteId: string;
  patientId: string;
  patientNotes: string;
  isGenerating: boolean;
  lastFocusedCursorOffset: number;
  allowErrorModal: boolean;
  patientNotesSavingStatus?: DataState;
}

@Injectable()
export class HealthHistoryNotesService extends ComponentStore<HealthHistoryNotesState> {
  public configService = inject(ConfigService);
  public tokenService = inject(TokenService);
  public dialog = inject(Dialog);
  public ngZone = inject(NgZone);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly voiceChannel = createVoiceChannel(VoiceInputType.VoiceTyping, DeepgramFeature.NOTE_VOICE);

  /** Holds the in-flight utterance while recording; the sink below writes it, the text field renders it. */
  public readonly transcriptionControl = new FormControl<string | null>('');
  private readonly dictation = new DictationSink(this.voiceChannel, this.transcriptionControl);
  public readonly dictationFinalized$ = this.dictation.finalized$;

  public readonly transcriptionStatus = computed<'idle' | 'loading' | 'recording'>(() => {
    if (this.voiceChannel.isLoading()) {
      return 'loading';
    }

    return this.voiceChannel.isListening() ? 'recording' : 'idle';
  });
  private readonly patientNotesDataService = inject(PatientNotesDataService);
  private readonly historyStackService = inject<HistoryStackService<PatientNotesHistoryElement>>(HistoryStackService);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);

  constructor() {
    super({
      siteId: '',
      patientId: '',
      patientNotes: '',
      isGenerating: false,
      lastFocusedCursorOffset: 0,
      allowErrorModal: true,
    });
  }

  private abortController = new AbortController();
  private abortSignal = this.abortController.signal;
  private hasPendingTypingDebounce = false;
  private readonly noteTypingSubject$ = new Subject<{ notes: string }>();

  private readonly enhanceRange = signal<{ textStart?: number; textEnd?: number; originalNote?: string }>({
    textStart: undefined,
    textEnd: undefined,
    originalNote: undefined,
  });

  // Accumulator for enhanced text chunks during streaming
  private enhancedTextAccumulator: string = '';

  // controlledNote$ is the note that is currently being edited, but it may not contain the changes which has been typed in manually in the text field.
  // because we want to keep the cursor position, we do not re-render the UI when the note is changed manually
  public controlledNote$ = new BehaviorSubject<string>('');
  public redoDisabledSignal = computed(() => this.historyStackService.isFirstPosition() || this.isGeneratingSignal());
  public undoDisabledSignal = computed(() => this.historyStackService.isLastPosition() || this.isGeneratingSignal());
  public isGeneratingSignal = computed(() => this.state().isGenerating);
  public currentHistoryElementSignal = computed(() => this.historyStackService.getCurrentItem());
  public lastFocusedCursorOffsetSignal = this.selectSignal((state) => state.lastFocusedCursorOffset);

  public readonly isPatientNotesSavingInProgress = this.selectSignal((state) => state.patientNotesSavingStatus === DataState.Loading);
  public readonly isPatientNotesSaved = this.selectSignal((state) => state.patientNotesSavingStatus === DataState.Fetched);
  public readonly patientNotes = computed(() => this.healthHistoryDialogService.viewModel().patientNotes);
  public readonly patientNotesLoading = computed(() => this.healthHistoryDialogService.viewModel().patientNotesLoading);

  public init = this.updater((_): HealthHistoryNotesState => {
    const initialPatientNotes = this.state().patientNotes.length > 0 ? this.state().patientNotes : this.patientNotes();

    this.setControlledNote(initialPatientNotes);
    this.updateLastFocusedCursorOffset(initialPatientNotes.length);

    this.historyStackService.init({
      patientNotes: initialPatientNotes,
      cursorOffset: initialPatientNotes.length,
    });

    return {
      siteId: this.healthHistoryDialogService.siteIdSignal(),
      patientId: this.healthHistoryDialogService.patientIdSignal(),
      patientNotes: initialPatientNotes,
      isGenerating: false,
      lastFocusedCursorOffset: initialPatientNotes.length,
      allowErrorModal: true,
    };
  });

  // Voice typing related functions:

  /**
   * The onStop splice is gone: text arrives through transcript$ as it is spoken and the text field splices it in,
   * emitting the merged note through manualTypeKeyUp -> noteTyping, the same debounced history-and-save path
   * manual typing uses. A failure closes the channel, which returns the button to idle.
   */
  public readonly toggleSpeechRecognitionListening = this.effect(($) =>
    $.pipe(
      tap(() => {
        if (this.voiceChannel.isListening() || this.voiceChannel.isLoading()) {
          this.voiceChannel.close();

          return;
        }

        // The control survives the previous session, so clear it or the next utterance appends to the last one.
        this.transcriptionControl.setValue('', { emitEvent: false });
        this.voiceChannel.open();
      }),
    ),
  );

  /** Used by the component when the dialog closes or the user clicks away mid-dictation. */
  public stopVoiceTyping(): void {
    this.voiceChannel.close();
  }

  /**
   * Reconciles controlledNote$ with the correctly-merged dictated text once recording stops. Guarded against a
   * no-op stop (no dictation, no pending typing debounce) so it does not manufacture a history entry or a save
   * when the user starts and stops without speaking. Also cancels a still-pending debounced noteTyping write so
   * it cannot later overwrite this commit with the stale text it captured before the final utterance arrived.
   */
  public commitDictatedText(notes: string): void {
    if (notes === this.controlledNote$.value && !this.hasPendingTypingDebounce) {
      return;
    }

    this.hasPendingTypingDebounce = false;
    this.setControlledNote(notes);
    this.addToHistoryStack({
      patientNotes: notes,
      cursorOffset: this.state().lastFocusedCursorOffset,
    });
    this.patchState({ patientNotes: notes });
    this.saveNotes();
  }

  public updateLastFocusedCursorOffset = this.updater((state, lastFocusedCursorOffset: number): HealthHistoryNotesState => {
    return { ...state, lastFocusedCursorOffset };
  });

  // AI related functions:

  private readonly appendEnhancedNote = (noteChunk: string): void => {
    // Accumulate the enhanced text chunks
    this.enhancedTextAccumulator += noteChunk;

    // Update last focused cursor offset accordingly
    const newCursorOffset = this.state().lastFocusedCursorOffset + noteChunk.length;
    this.updateLastFocusedCursorOffset(newCursorOffset);

    // Update the display but don't update the history stack yet - that happens on completion
    this.controlledNote$.next(this.enhancedTextAccumulator);
    this.setPatientNotes(this.enhancedTextAccumulator);
  };

  private readonly appendEnhancedNoteWithTextRange = (noteChunk: string, textStart: number, textEnd: number): void => {
    const enhanceRangeValue = this.enhanceRange();
    const originalNote = enhanceRangeValue.originalNote || '';

    // Accumulate the enhanced text chunks
    this.enhancedTextAccumulator += noteChunk;

    // Update last focused cursor offset accordingly
    const newCursorOffset = textStart + this.enhancedTextAccumulator.length;
    this.updateLastFocusedCursorOffset(newCursorOffset);

    // Construct and display the note with the accumulated enhanced text
    // but don't update the history stack yet - that happens on completion
    const newNoteText = originalNote.slice(0, textStart) + this.enhancedTextAccumulator + originalNote.slice(textEnd);
    this.controlledNote$.next(newNoteText);
    this.setPatientNotes(newNoteText);
  };

  public abortEnhance = this.effect(($) => {
    return $.pipe(
      withLatestFrom(this.select((state) => state.isGenerating)),
      tap(([_, isGenerating]) => {
        if (isGenerating) {
          this.abortController.abort();
        }
      }),
    );
  });

  // TODO: fix error handling, should be able to do another enhance after error
  public enhanceNote = this.effect<{
    noteInput: string;
    enhanceType: EnhancePatientNotePromptType;
    textStart?: number;
    textEnd?: number;
    fullNote?: string;
  }>(
    (
      $: Observable<{
        noteInput: string;
        enhanceType: EnhancePatientNotePromptType;
        textStart?: number;
        textEnd?: number;
        fullNote?: string;
      }>,
    ) => {
      const encoder = new TextDecoder();
      return $.pipe(
        tap(({ fullNote, textStart, textEnd }) => {
          // Store the original note and range info
          this.enhanceRange.set({
            textStart,
            textEnd,
            originalNote: textStart !== undefined && textEnd !== undefined ? fullNote : undefined,
          });

          // Reset the accumulator for new enhancement
          this.enhancedTextAccumulator = '';

          this.patchState({ isGenerating: true });
        }),
        concatLatestFrom(() => this.tokenService.getAccessToken$({ isUserActivity: true })),
        switchMap(([{ noteInput, enhanceType }, token]) => {
          return from(
            fetch(`${this.configService.config()?.apiBasePath}/v1/patient-notes/enhance-stream`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                note: noteInput,
                enhanceType: enhanceType,
                siteId: this.get().siteId,
              }),
            }),
          ).pipe(
            catchError((error) => {
              console.error('Error while enhancing note', error);
              this.toastService.error(this.translateService.instant('health_history_notes_section_enhance_error'));
              this.abortController = new AbortController();
              this.abortSignal = this.abortController.signal;
              this.patchState({ isGenerating: false });
              return EMPTY;
            }),
          );
        }),
        switchMap((response) => {
          if (!response.ok) {
            return from(response.json()).pipe(
              switchMap((responseJson) => {
                if (responseJson.statusCode === 400) {
                  this.toastService.warning(this.translateService.instant('notes_empty_note_error_message'));
                  this.addToHistoryStack({
                    patientNotes: '',
                    cursorOffset: 0,
                  });
                }
                if (responseJson.statusCode === 403) {
                  this.toastService.error(this.translateService.instant('notes_ai_token_exceeded'));
                }
                this.patchState({ isGenerating: false });
                return EMPTY;
              }),
            );
          }

          return response.body
            ? fromReadableStream(response.body, { ngZone: this.ngZone, signal: this.abortSignal }).pipe(
                tap({
                  next: (chunk) => {
                    const text = encoder.decode(chunk);
                    if (this.enhanceRange().textStart !== undefined && this.enhanceRange().textEnd !== undefined) {
                      this.appendEnhancedNoteWithTextRange(text, this.enhanceRange().textStart!, this.enhanceRange().textEnd!);
                    } else {
                      this.appendEnhancedNote(text);
                    }
                  },
                  error: () => {
                    console.error('Error while enhancing note');
                    this.toastService.error(this.translateService.instant('health_history_notes_section_enhance_error'));
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });
                  },
                  complete: () => {
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });

                    // Now that enhancement is complete, add the final result to history stack
                    const finalNote = this.controlledNote$.value;
                    this.addToHistoryStack({
                      patientNotes: finalNote,
                      cursorOffset: this.state().lastFocusedCursorOffset,
                    });

                    this.saveNotes();
                  },
                }),
              )
            : EMPTY;
        }),
      );
    },
  );

  // History related functions:

  private readonly addToHistoryStack = (note: PatientNotesHistoryElement): void => {
    this.historyStackService.push(note);
  };

  public setControlledNote(note: string): void {
    this.controlledNote$.next(note);
  }

  public handleNoteTyping = this.effect((_$) => {
    return this.noteTypingSubject$.pipe(
      tap(() => {
        this.hasPendingTypingDebounce = true;
      }),
      //we have autosave, so here we apply some debounce time to avoid saving the note too often
      debounceTime(MODIFY_NOTE_DEBOUNCE_TIME_MS),
      filter(() => this.hasPendingTypingDebounce),
      tap(({ notes }) => {
        this.hasPendingTypingDebounce = false;
        // here we do not modify the note purposefully, because we want to keep the cursor position. (otherwise the component would be re-rendered)
        this.addToHistoryStack({
          patientNotes: notes,
          cursorOffset: this.state().lastFocusedCursorOffset,
        });
      }),
      tap(({ notes }) => {
        this.patchState({ patientNotes: notes });
        this.saveNotes();
      }),
    );
  });

  public noteTyping(data: { notes: string }): void {
    this.noteTypingSubject$.next(data);
  }

  public undo = (): void => {
    const currentItem = this.historyStackService.undo();
    if (currentItem) {
      this.setControlledNote(currentItem.patientNotes);
      this.updateLastFocusedCursorOffset(currentItem.cursorOffset);
      this.saveNotes();
    }
  };

  public redo = (): void => {
    const currentItem = this.historyStackService.redo();
    if (currentItem) {
      this.setControlledNote(currentItem.patientNotes);
      this.updateLastFocusedCursorOffset(currentItem.cursorOffset);
      this.saveNotes();
    }
  };

  public resetHistory = (): void => {
    this.historyStackService.reset();
  };

  // Save note related functions:

  public readonly setPatientNotesSavingStatus = this.updater((state, status: DataState): HealthHistoryNotesState => {
    return {
      ...state,
      patientNotesSavingStatus: status,
    };
  });

  public readonly setPatientNotes = this.updater((state, patientNotes: string): HealthHistoryNotesState => {
    return {
      ...state,
      patientNotes,
    };
  });

  public saveNotes = this.effect((note$) =>
    note$.pipe(
      tap(() => this.setPatientNotesSavingStatus(DataState.Loading)),
      debounceTime(MODIFY_NOTE_DEBOUNCE_TIME_MS),
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      switchMap(([_, siteId, patientId]) => {
        const note = this.historyStackService.getCurrentItem()?.patientNotes ?? this.state().patientNotes;
        return iif(
          () => siteId === routes.showcaseSiteId,
          of(EMPTY),
          this.patientNotesDataService.setPatientNote(siteId, patientId, { text: note }),
        ).pipe(
          tapResponse(
            () => {
              this.setPatientNotesSavingStatus(DataState.Fetched);
              if (siteId === routes.showcaseSiteId) {
                this.healthHistoryDialogService.setPatientNotes([
                  { id: String(crypto.getRandomValues(new Uint32Array(1))[0]), text: note, type: 3 },
                ]);
              }
            },
            (error: HttpErrorResponse) => {
              console.error(error);
              this.setPatientNotesSavingStatus(DataState.Initial);
            },
            () => {
              if (siteId === routes.showcaseSiteId) return;

              this.healthHistoryDialogService.getPatientNotes();
            },
          ),
        );
      }),
    ),
  );
}
